import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { UserContext } from "../common/UserContext";
import config from "../../envconfig";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { IoCaretUpOutline, IoCaretDownOutline } from "react-icons/io5";
import { set } from "date-fns";
import { forwardRef } from "react";
import CustomSingleValue from '../common/CustomSingleValue';

// ──────────────────────────────────────────────────────────
// Masked date input — enforces DD/MM/YYYY format while typing
// minDate: dates before this are rejected inline (no toast needed here)
// onValidDate(date): called when user finishes typing a valid, allowed date
// onInvalidDate(date): called when date is complete but before minDate
// ──────────────────────────────────────────────────────────
const MaskedDateInput = forwardRef((
  { value, onClick, onChange, onBlur, disabled, className, style,
    placeholderText, minDate, onValidDate, onInvalidDate },
  ref
) => {
  const [inputVal, setInputVal] = React.useState(value || "");

  // Sync when react-datepicker changes value externally (calendar click / reset)
  React.useEffect(() => {
    setInputVal(value || "");
  }, [value]);

  const handleKeyDown = (e) => {
    const allowed = ["Backspace", "Delete", "Tab", "Escape", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
    if (allowed.includes(e.key)) return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
  };

  const handleChange = (e) => {
    let raw = e.target.value.replace(/\D/g, "").slice(0, 8);

    // Validate day (first 2 digits)
    if (raw.length >= 2) {
      const dd = parseInt(raw.slice(0, 2), 10);
      if (dd < 1 || dd > 31) raw = raw.slice(0, 1);
    }
    // Validate month (digits 3-4)
    if (raw.length >= 4) {
      const mm = parseInt(raw.slice(2, 4), 10);
      if (mm < 1 || mm > 12) raw = raw.slice(0, 3);
    }

    // Build display string with / separators
    let display = "";
    if (raw.length <= 2) display = raw;
    else if (raw.length <= 4) display = raw.slice(0, 2) + "/" + raw.slice(2);
    else display = raw.slice(0, 2) + "/" + raw.slice(2, 4) + "/" + raw.slice(4);

    setInputVal(display);

    if (raw.length === 8) {
      const dd = parseInt(raw.slice(0, 2), 10);
      const mm = parseInt(raw.slice(2, 4), 10);
      const yyyy = parseInt(raw.slice(4, 8), 10);
      const parsed = new Date(yyyy, mm - 1, dd);

      // Confirm it's a real calendar date (not e.g. Feb 30)
      const isCalendarValid =
        parsed.getFullYear() === yyyy &&
        parsed.getMonth() === mm - 1 &&
        parsed.getDate() === dd;

      if (!isCalendarValid) {
        // Nonsense date — just clear silently
        setInputVal("");
        return;
      }

      // Check minDate constraint
      if (minDate) {
        const min = new Date(minDate);
        min.setHours(0, 0, 0, 0);
        if (parsed < min) {
          setInputVal("");             // clear the typed value immediately
          if (onInvalidDate) onInvalidDate(parsed);
          return;
        }
      }

      // ✅ Valid & allowed date — notify parent directly
      if (onValidDate) onValidDate(parsed);
    }
    // For partial input (< 8 digits) just show what's being typed — no action
  };

  return (
    <input
      ref={ref}
      type="text"
      className={className}
      style={{ ...style, cursor: disabled ? "not-allowed" : "text" }}
      value={inputVal}
      onClick={onClick}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={onBlur}
      disabled={disabled}
      placeholder={placeholderText || "DD/MM/YYYY"}
      maxLength={10}
      autoComplete="off"
    />
  );
});


const SchemeMaster = () => {
  const navigate = useNavigate();
  const { schemeId: paramId } = useParams();
  let schemeId = paramId ? Number(paramId) : null;
  const isEditMode = !!schemeId && !isNaN(schemeId);


  const [regions, setRegions] = useState([]);
  const [scps, setScps] = useState([]);
  const [products, setProducts] = useState([]);
  const [scpOptions, setScpOptions] = useState([]);

  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedScps, setSelectedScps] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedFreeProducts, setSelectedFreeProducts] = useState([]);

  const [productQuantities, setProductQuantities] = useState({});
  const [freeProductQuantities, setFreeProductQuantities] = useState({});

  const [schemeType, setSchemeType] = useState("Discount");
  const [selectedSchemeTypeId, setSelectedSchemeTypeId] = useState(1);
  const [discountType, setDiscountType] = useState("Percentage");
  const [discountValue, setDiscountValue] = useState("");

  const [giftArticle, setGiftArticle] = useState("");
  const [schemeName, setSchemeName] = useState("");
  const [schemeDescription, setSchemeDescription] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [datePickerKey, setDatePickerKey] = useState(0);  // incremented to force-reset MaskedDateInput
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSelectOpen, setIsSelectOpen] = useState(null);
  const [isCombination, setIsCombination] = useState(false);
  const [selectedSCP, setSelectedSCP] = useState(null);
  const [status, setStatus] = useState("Active");
  const [oldStatus, setOLDStatus] = useState(0);
  const [selectedStatusId, setSelectedStatusId] = useState(1);
  const [oldstatus, setOldStatus] = useState(0);
  const [oldendDate, setOldEndDate] = useState(null);
  // SEPARATE STATES
  const [requiredErrors, setRequiredErrors] = useState({});     // for asterisk (*)

  const { UM_CompanyID, UM_UserId, UM_CompanyGrpID } = useContext(UserContext);
  const isDisabled = isEditMode || loading;

  // --------------------------------------------------------------
  // 1. LOAD MASTER DATA: Products + SCP List (once)
  // --------------------------------------------------------------
  const [masterDataLoaded, setMasterDataLoaded] = useState(false);
  const [showconfimationModal, setshowconfimationModal] = useState(false);
  const scrollRef = useRef(null);

  //   useEffect(() => {
  //   if (!isEditMode && scpOptions?.length > 0) {
  //     const defaultSCP = scpOptions.find(o => o.value === 1);
  //     setSelectedSCP(defaultSCP || null);
  //   }
  // }, [isEditMode, scpOptions]);


  // --------------------------------------------------------------
  // RESET FORM when navigating from Edit (/SchemeMaster/:id)
  // to Add (/SchemeMaster) via sidebar — same component reused by React
  // --------------------------------------------------------------
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // paramId changed → if now undefined, moved to Add mode: full reset
    if (!paramId) {
      setSelectedSCP(null);
      setSchemeName("");
      setSchemeDescription("");
      setSchemeType("Discount");
      setSelectedSchemeTypeId(1);
      setDiscountType("Percentage");
      setDiscountValue("");
      setStartDate(null);
      setEndDate(null);
      setStatus("Active");
      setSelectedStatusId(1);
      setSelectedRegions([]);
      setSelectedScps([]);
      setSelectedProducts([]);
      setProductQuantities({});
      setSelectedFreeProducts([]);
      setFreeProductQuantities({});
      setGiftArticle("");
      setIsCombination(false);
      setRequiredErrors({});
      setError(null);
    }
  }, [paramId]);

  useEffect(() => {
    if (!UM_CompanyID) return;


    const loadMaster = async () => {
      try {
        setLoading(true);
        const [prodRes, scpRes] = await Promise.all([
          fetch(`${config.API_URL}/Scheme/product`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ companyid: UM_CompanyID }),
          }),
          fetch(`${config.API_URL}/Scheme/scplist`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ CompanyID: UM_CompanyID }),
          }),
        ]);

        const [prodJson, scpJson] = await Promise.all([prodRes.json(), scpRes.json()]);

        const prods = Array.isArray(prodJson.data) ? prodJson.data : [];
        const scps = scpJson.success
          ? scpJson.data.map(scp => ({
            value: scp.SCPID,
            label: scp.SCPName,
          }))
          : [];

        setProducts(prods);
        setScpOptions(scps);
        setMasterDataLoaded(true);
      } catch (e) {
        //toast.error("Failed to load master data");
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadMaster();
  }, [UM_CompanyID]);

  // --------------------------------------------------------------
  // 2. EDIT MODE: Load scheme only after master data
  // --------------------------------------------------------------
  useEffect(() => {
    if (!isEditMode || !masterDataLoaded) return;

    setLoading(true);

    const loadScheme = async () => {
      try {
        const resp = await fetch(`${config.API_URL}/Scheme/get/${schemeId}`);
        const json = await resp.json();
        if (!json.success) throw new Error(json.message || "Failed to load scheme");

        const d = json.data;
        console.log("d", d)
        const toNum = arr => arr.map(x => Number(x));

        const selProdIds = toNum(d.selectedProducts);
        const selFreeIds = toNum(d.selectedFreeProducts);
        const selRegionIds = toNum(d.selectedRegions);
        const selScpIds = toNum(d.selectedScps);

        setSchemeName(d.schemeName || "");
        setSchemeDescription(d.schemeDescription || "");
        setSelectedSCP(scpOptions.find(o => o.value === d.selectedScpId) || null);
        setSchemeType(["", "Discount", "Free Product", "Gift Article"][d.schemeType]);
        setSelectedSchemeTypeId(d.schemeType);
        setGiftArticle(d.giftArticle || "");
        const parseFixedDate = (dateStr) => {
          if (!dateStr) return null;
          const d = new Date(dateStr);
          d.setDate(d.getDate() + 1);
          return d;
        };

        setStartDate(parseFixedDate(d.startDate));
        setEndDate(parseFixedDate(d.endDate));

        setOldEndDate(parseFixedDate(d.endDate))

        setOldStatus(d.status);

        setStatus(d.status === 1 ? "Active" : "Inactive");
        setSelectedStatusId(d.status);
        setIsCombination(!!d.isCombination);

        if (d.schemeType === 1) {
          const type = d.discountType === 1 ? "Percentage" : d.discountType === 2 ? "Amount" : "Percentage";
          const val = d.discountPercentage ?? "";
          setDiscountType(type);
          setDiscountValue(val);
        }

        const restore = (ids, allFlag, setter) => {
          const clean = ids.filter(x => x !== "All");
          if (allFlag && clean.length) setter([...clean, "All"]);
          else setter(clean);
        };

        restore(selRegionIds, d.regionAllSelected, setSelectedRegions);
        restore(selScpIds, d.scpAllSelected, setSelectedScps);
        restore(selProdIds, d.productAllSelected, setSelectedProducts);
        restore(selFreeIds, d.freeProductAllSelected, setSelectedFreeProducts);

        setProductQuantities(d.productQuantities || {});
        setFreeProductQuantities(d.freeProductQuantities || {});
      } catch (err) {
        //toast.error(err.message || "Failed to load scheme");
      } finally {
        setLoading(false);
      }
    };

    loadScheme();
  }, [isEditMode, schemeId, masterDataLoaded, scpOptions]);

  // --------------------------------------------------------------
  // 3. REGIONS → based on selected SCP
  // --------------------------------------------------------------
  useEffect(() => {
    const controller = new AbortController();
    const fetchRegions = async () => {
      console.log("selectedSCP in fetchRegions", selectedSCP?.value, selectedRegions);
      if (!selectedSCP?.value) {
        setRegions([]);
        if (!isEditMode) {
          setSelectedRegions([]);
          setScps([]);
          setSelectedScps([]);
        }
        return;
      }
      try {
        setLoading(true);
        const response = await fetch(`${config.API_URL}/Scheme/region`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ RUTL_SCPID: selectedSCP.value }),
          signal: controller.signal,
        });
        const data = await response.json();
        setRegions(Array.isArray(data.data) ? data.data : []);
        if (!isEditMode) {
          setSelectedRegions([]);
          setScps([]);
          setSelectedScps([]);
        }
        console.log("After setting SCPS to empty", scps);
      } catch (e) {
        if (e.name !== "AbortError") setError("Failed to load regions");
      } finally {
        setLoading(false);
      }
    };
    fetchRegions();
    return () => controller.abort();
  }, [selectedSCP?.value]);


  // --------------------------------------------------------------
  // 4. SCPs → based on selected regions
  // --------------------------------------------------------------
  const fetchScps = useCallback(async () => {
    console.log("selectedRegions in fetchScps", selectedRegions);
    const ids = selectedRegions.filter(id => id !== "All");
    if (!ids.length || !selectedSCP?.value) {
      setScps([]);
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(`${config.API_URL}/Scheme/toscp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ routeid: ids, scpid: selectedSCP.value }),
      });
      const data = await response.json();
      const scpData = data.data || data;
      if (Array.isArray(scpData)) {
        const map = new Map();
        scpData.forEach(s => {
          if (s.SCPM_ID && !map.has(s.SCPM_ID))
            map.set(s.SCPM_ID, { SCPM_ID: s.SCPM_ID, SCPM_Name: s.SCPM_Name });
        });
        setScps(Array.from(map.values()));
      }
    } catch (e) {
      if (e.name !== "AbortError") setError("Failed to load SCPs");
    } finally {
      setLoading(false);
    }
  }, [selectedRegions]);

  useEffect(() => {
    fetchScps();
  }, [fetchScps]);

  // --------------------------------------------------------------
  // 5. AUTO-GENERATE DESCRIPTION
  // --------------------------------------------------------------
  useEffect(() => {
    const generate = () => {
      if (!selectedProducts.length || selectedProducts.every(id => id === "All") || (discountValue === "" && selectedFreeProducts.length === 0 && !giftArticle)) {
        setSchemeDescription("");
        return;
      }
      console.log("discountValue", discountValue)
      const getName = id =>
        products.find(p => p.PL_ProductId === id)?.PL_ProductName || id;

      const joiner = isCombination ? " and " : " or ";

      const buy = selectedProducts
        .filter(id => id !== "All")
        .map(id => {
          const qty = productQuantities[id] || "1";
          return `${qty === "1" ? "1 unit" : `${qty} units`} of ${getName(id)}`;
        })
        .join(joiner);

      let desc = `Buy ${buy}`;

      if (schemeType === "Discount") {
        if (discountType === "Percentage" && discountValue)
          desc += `, get ${discountValue}% discount`;
        else if (discountType === "Amount" && discountValue)
          desc += `, get Rs. ${discountValue} discount`;
      } else if (schemeType === "Free Product" && selectedFreeProducts.length) {
        const free = selectedFreeProducts
          .filter(id => id !== "All")
          .map(id => {
            const qty = freeProductQuantities[id] || "1";
            return `${qty} unit${qty !== "1" ? "s" : ""} of ${getName(id)}`;
          })
          .join(" and ");
        desc += `, get ${free} free`;
      } else if (schemeType === "Gift Article" && giftArticle) {
        desc += `, get ${giftArticle} free`;
      } else {
        desc = "";
      }
      setSchemeDescription(desc);
    };
    generate();
  }, [
    selectedProducts,
    productQuantities,
    isCombination,
    schemeType,
    discountType,
    discountValue,
    selectedFreeProducts,
    freeProductQuantities,
    giftArticle,
    products,
  ]);

  // --------------------------------------------------------------
  // 6. TOGGLE ALL LOGIC (reusable)
  // --------------------------------------------------------------
  const isAllChecked = (selected, source, idKey) => {
    if (!source.length) return false;
    const clean = selected.filter(x => x !== "All");
    return source.every(s => clean.includes(s[idKey]));
  };

  const toggleAll = (selected, setSelected, source, idKey, qtyMap, setQtyMap, freeProduct = 0) => {
    console.log(idKey, setQtyMap);
    if (isAllChecked(selected, source, idKey)) {
      setSelected([]);
      setQtyMap && setQtyMap({});
    } else {
      const ids = source.map(s => s[idKey]);
      setSelected([...ids, "All"]);
      if (setQtyMap) {
        const map = {};
        ids.forEach(id => (map[id] = qtyMap[id] || "1"));
        setQtyMap(map);
      }
    }
    if (idKey === 'PL_ProductId' && !freeProduct) {
      setRequiredErrors(p => ({ ...p, Products: false, ProductQuantities: false }));

    } else if (idKey === 'SCPM_ID') {
      setRequiredErrors(p => ({ ...p, Scps: false }));
    }
    else if (idKey === 'RUTL_ID') {
      setRequiredErrors(p => ({ ...p, Regions: false }));
    }
    else if (idKey === 'PL_ProductId' && freeProduct === 1) {
      setRequiredErrors(p => ({ ...p, FreeProducts: false, FreeProductQuantities: false }));
    }
  };

  // --------------------------------------------------------------
  // 7. HANDLERS
  // --------------------------------------------------------------
  const handleRegionChange = regionId => {
    console.log("SELECTED REGIONS IN REGION CHANGE", selectedRegions);
    console.log("SELECTED SCP IN REGION CHANGE", selectedScps);
    if (regionId === "All") {
      toggleAll(selectedRegions, setSelectedRegions, regions, "RUTL_ID");
    } else {
      setSelectedRegions(prev =>
        prev.includes(regionId)
          ? prev.filter(id => id !== regionId && id !== "All")
          : [...prev.filter(id => id !== "All"), regionId]
      );
    }
    // if (selectedRegions.filter(id => id !== "All").length > 0) {
    setRequiredErrors(p => ({ ...p, Regions: false }));
    // } 
  };

  useEffect(() => {
    const validScpIds = scps
      .filter(s => selectedRegions.includes(s.RUTL_ID))
      .map(s => s.SCPM_ID);
    if (!isEditMode) {
      setSelectedScps(prev => prev.filter(id => validScpIds.includes(id)));
    }
  }, [selectedRegions, scps]);


  const handleScpChange = scpId => {
    if (scpId === "All") {
      toggleAll(selectedScps, setSelectedScps, scps, "SCPM_ID");
    } else {
      setSelectedScps(prev =>
        prev.includes(scpId)
          ? prev.filter(id => id !== scpId && id !== "All")
          : [...prev.filter(id => id !== "All"), scpId]
      );
    }
    // if (selectedScps.filter(id => id !== "All").length > 0) {
    setRequiredErrors(p => ({ ...p, Scps: false }));
    // }
  };

  const handleProductChange = productId => {
    setRequiredErrors(p => ({ ...p, Products: false }));
    if (productId === "All") {
      toggleAll(selectedProducts, setSelectedProducts, products, "PL_ProductId", productQuantities, setProductQuantities);
    } else {
      const updated = selectedProducts.includes(productId)
        ? selectedProducts.filter(id => id !== productId && id !== "All")
        : [...selectedProducts.filter(id => id !== "All"), productId];

      setProductQuantities(prev => {
        const n = { ...prev };
        if (!updated.includes(productId)) delete n[productId];
        else n[productId] = n[productId] || "1";
        return n;
      });

      setSelectedProducts(updated);
    }
    // if (selectedProducts.filter(id => id !== "All").length > 0) {

    // }
  };

  const handleFreeProductChange = productId => {
    if (productId === "All") {
      toggleAll(selectedFreeProducts, setSelectedFreeProducts, products, "PL_ProductId", freeProductQuantities, setFreeProductQuantities);
    } else {
      const updated = selectedFreeProducts.includes(productId)
        ? selectedFreeProducts.filter(id => id !== productId && id !== "All")
        : [...selectedFreeProducts.filter(id => id !== "All"), productId];

      const allIds = products.map(p => p.PL_ProductId);
      if (allIds.every(id => updated.includes(id))) updated.push("All");

      setFreeProductQuantities(prev => {
        const n = { ...prev };
        if (!updated.includes(productId)) delete n[productId];
        else n[productId] = n[productId] || "1";
        return n;
      });

      setSelectedFreeProducts(updated);
    }
    // if (selectedFreeProducts.filter(id => id !== "All").length > 0) {
    setRequiredErrors(p => ({ ...p, FreeProducts: false }));
    // }
  };

  const handleProductQuantityChange = (pid, v) => {
    // Allow only digits up to 8 characters
    if (!/^\d{0,8}$/.test(v)) return;

    // Prevent single 0
    if (v === "0") return;

    // Prevent numbers starting with 0 like "05", "007"
    if (v.startsWith("0") && v.length > 1) return;

    setProductQuantities(prev => ({ ...prev, [pid]: v }));
  };


  const handleFreeProductQuantityChange = (pid, v) => {
    // Allow only digits up to 8 characters
    if (!/^\d{0,8}$/.test(v)) return;

    // Prevent single 0
    if (v === "0") return;

    // Prevent leading zeros like "05", "007"
    if (v.startsWith("0") && v.length > 1) return;

    setFreeProductQuantities(prev => ({ ...prev, [pid]: v }));
  };


  const handleChange = (field, value) => {
    let trimmedValue = "";
    const errors = {};

    switch (field) {
      case "SCP":
        // ✅ Set SCP
        setSelectedSCP(value);
        setRequiredErrors(p => ({ ...p, SCP: false }));
        errors.SCP = false;

        // ✅ Reset all other fields except SCP & available regions
        setSchemeName("");
        setSchemeType("Discount");
        setSelectedSchemeTypeId(1);
        setDiscountType("Percentage");
        setDiscountValue("");
        setStartDate(null);
        setEndDate(null);
        setStatus("Active");
        setSelectedStatusId(1);
        // ⛔ Keep region data — don’t reset these:
        // setSelectedRegions([]);
        // setAvailableRegions([]);
        // ✅ But do reset others:
        setSelectedScps([]);
        setSelectedProducts([]);
        setProductQuantities({});
        setSelectedFreeProducts([]);
        setFreeProductQuantities({});
        setGiftArticle("");
        setIsCombination(false);
        //setRequiredErrors({});
        setError(null);
        break;

      case "SchemeName":
        // allow only safe filename characters
        const allowed = /^[A-Za-z0-9 ~`!@#$%^&()_+\-=\[\];',.{}]*$/;

        let cleanedValue = value.trimStart().replace(/\s{2,}/g, " ");
        trimmedValue = cleanedValue?.slice(0, 45);

        if (allowed.test(trimmedValue)) {
          setSchemeName(trimmedValue);
          errors.SchemeName = false;
        }
        break;

      case "SchemeType":
        setSchemeType(value.value);
        setSelectedSchemeTypeId(value.id);
        setDiscountType("Percentage");
        setDiscountValue("");
        setSelectedFreeProducts([]);
        setGiftArticle("");
        setFreeProductQuantities({});
        setIsCombination(false);
        setRequiredErrors(p => ({
          ...p,
          Discount: false,
          FreeProducts: false,
          GiftArticle: false,
          ProductQuantities: false,
          FreeProductQuantities: false,
        }));
        break;

      case "Status":
        setStatus(value.label);
        setSelectedStatusId(value.id);
        setRequiredErrors(p => ({ ...p, Status: false }));
        errors.Status = false;
        break;

      case "DiscountType":
        setDiscountType(value);
        setDiscountValue("");
        setRequiredErrors(p => ({ ...p, Discount: false }));
        break;

      case "DiscountValue":
        if (discountType === "Amount") {
          // Allow only digits, up to 7
          const amountRegex = /^[0-9]{0,7}$/;
          if (!amountRegex.test(value)) return; // 🚫 Ignore invalid input
          setDiscountValue(value);
          errors.DiscountValue = false;
        } else if (discountType === "Percentage") {
          // Allow 1–100 only, optionally with up to 2 decimals
          const percentRegex = /^(100(\.0{0,2})?|([1-9]?[0-9])(\.[0-9]{0,2})?)$/;

          // First check regex
          if (value === "" || percentRegex.test(value)) {
            // 🚫 Block exactly 100 or 100.0 or 100.00
            if (Number(value) === 100) return;

            setDiscountValue(value);
            errors.DiscountValue = false;
          } else {
            return; // 🚫 Ignore invalid input
          }
        }
        if (value && Number(value) > 0 && (discountType !== "Percentage" || Number(value) <= 100)) {
          setRequiredErrors(p => ({ ...p, Discount: false }));
        }
        break;


      case "GiftArticle":
        let cleanedGift = value.trimStart().replace(/\s{2,}/g, " ");
        trimmedValue = cleanedGift?.slice(0, 45);
        setGiftArticle(trimmedValue);

        if (trimmedValue) {
          setRequiredErrors(p => ({ ...p, GiftArticle: false }));
          errors.GiftArticle = false;
        }
        break;

      default:
        break;
    }

    if (Object.keys(errors).length) {
      setRequiredErrors(prev => ({ ...prev, ...errors }));
    }
  };

  const handleStartDateChange = date => {
    if (!date) { setStartDate(null); return; }
    // Validate it's a real Date object
    if (!(date instanceof Date) || isNaN(date.getTime())) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Reject past dates — both typed and calendar-selected
    if (date < today) {
      toast.error("Start Date cannot be a past date.");
      setDatePickerKey(k => k + 1); // force MaskedDateInput to clear
      return;
    }

    setStartDate(date);
    setRequiredErrors(p => ({ ...p, StartDate: false }));
    // If end date is now before the new start date, clear it
    if (endDate && endDate < date) setEndDate(null);
  };


  const handleEndDateChange = (date) => {
    if (!date) { setEndDate(null); return; }
    // Validate it's a real Date object
    if (!(date instanceof Date) || isNaN(date.getTime())) return;

    // Normalize current date (remove time)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Reject past dates — both typed and calendar-selected
    if (date < today) {
      toast.error("End Date cannot be a past date.");
      setDatePickerKey(k => k + 1); // force MaskedDateInput to clear
      return;
    }

    // Ensure end date is not before start date
    if (startDate && date < startDate) {
      toast.error("End Date must be on or after the Start Date.");
      setDatePickerKey(k => k + 1); // force MaskedDateInput to clear
      return;
    }

    setEndDate(date);
    setRequiredErrors((p) => ({ ...p, EndDate: false }));
  };


  // --------------------------------------------------------------
  // 8. SAVE
  // --------------------------------------------------------------
  const validate = () => {
    const errors = {};

    if (!selectedSCP) errors.SCP = true;
    if (!schemeName) errors.SchemeName = true;
    if (!startDate) errors.StartDate = true;
    if (!endDate) errors.EndDate = true;
    if (selectedRegions.length === 0) errors.Regions = true;
    if (selectedScps.length === 0) errors.Scps = true;
    if (selectedProducts.filter(id => id !== "All").length === 0) errors.Products = true;

    if (schemeType === "Discount") {
      if (!discountValue || Number(discountValue) <= 0) {
        errors.Discount = true;
      } else if (discountType === "Percentage" && Number(discountValue) > 100) {
        errors.Discount = true;
      }
    }

    if (schemeType === "Free Product") {
      if (selectedFreeProducts.filter(id => id !== "All").length === 0)
        errors.FreeProducts = true;
      const badQty = selectedProducts.some(id => id !== "All" && (!productQuantities[id] || Number(productQuantities[id]) <= 0));
      if (badQty) errors.ProductQuantities = true;
      const badFree = selectedFreeProducts.some(id => id !== "All" && (!freeProductQuantities[id] || Number(freeProductQuantities[id]) <= 0));
      if (badFree) errors.FreeProductQuantities = true;
    }

    if (schemeType === "Gift Article" && !giftArticle) errors.GiftArticle = true;

    // ─── Stop if either required or validation errors ───
    if (Object.keys(errors).length > 0) {
      setRequiredErrors(errors);
      return false;   // 🔥 IMPORTANT
    }

    return true;
  };

  const handleConfirmation = () => {
    const isValid = validate();

    if (!isValid) return;

    setshowconfimationModal(true);
  }

  const handleSave = async () => {

    const isValid = validate();

    if (!isValid) return;

    const clean = arr => arr.filter(id => id !== "All");

    const formatDate = (d) => {
      if (!d) return null;
      if (typeof d === "string") return d; // already "YYYY-MM-DD"
      return d.toLocaleDateString("en-CA"); // convert Date → "YYYY-MM-DD"
    };

    const oldEndDateValue = formatDate(oldendDate);
    const newEndDate = formatDate(endDate);

    const payload = {
      schemeId: isEditMode ? schemeId : undefined,
      schemeName,
      schemeDescription,
      selectedScpId: selectedSCP?.value,
      UM_UserId,
      selectedRegions: clean(selectedRegions),
      selectedScps: clean(selectedScps),
      selectedProducts: clean(selectedProducts),
      productQuantities,
      schemeType: selectedSchemeTypeId,
      discountType: schemeType === "Discount" ? (discountType === "Percentage" ? 1 : 2) : null,
      discountPercentage: schemeType === "Discount" ? discountValue : null,
      selectedFreeProducts: clean(selectedFreeProducts),
      freeProductQuantities: freeProductQuantities,
      giftArticle,
      startDate: startDate ? startDate.toLocaleDateString('en-CA') : null, // 'YYYY-MM-DD'
      endDate: endDate ? endDate.toLocaleDateString('en-CA') : null,
      status: selectedStatusId,
      regionAllSelected: isAllChecked(selectedRegions, regions, "RUTL_ID"),
      scpAllSelected: isAllChecked(selectedScps, scps, "SCPM_ID"),
      productAllSelected: isAllChecked(selectedProducts, products, "PL_ProductId"),
      freeProductAllSelected: schemeType === "Free Product" ? isAllChecked(selectedFreeProducts, products, "PL_ProductId") : false,
      isCombination,
      UM_CompanyGrpID,
      UM_CompanyID,
      OldStatus: oldStatus,
    };

    console.log("payload", payload)

    const url = isEditMode ? `${config.API_URL}/Scheme/update` : `${config.API_URL}/Scheme/save`;

    try {
      setLoading(true);
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok && data.success) {

        let finalSchemeId = isEditMode ? schemeId : data.schemeId;


        const emailPayload = {
          schemeId: finalSchemeId,
          endDate: newEndDate,
          selectedScps: clean(selectedScps),
          status: selectedStatusId
        };



        if (isEditMode) {
          if (oldEndDateValue !== newEndDate || oldstatus !== selectedStatusId) {
            console.log("YES → Insert email transaction", emailPayload);
            await fetch(`${config.API_URL}/Scheme/schemetransactionupdate`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(emailPayload),
            });
          }
        }
        else {
          // New scheme → always insert once
          console.log("NEW SCHEME → Insert email transaction", emailPayload);
          await fetch(`${config.API_URL}/Scheme/schemetransactionupdate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(emailPayload),
          });
        }



        toast.success(isEditMode ? `Scheme : ${schemeName} updated successfully.` : `Scheme : ${schemeName} saved successfully.`, {
          onClose: () => {
            if (isEditMode) {
              navigate(`/SchemeMaster`);
              schemeId = null;
            }
            setLoading(false);
            handleReset();
          },
          autoClose: 2000,
        });
        setRequiredErrors({});
        if (isEditMode) {
          // navigate(`/SchemeMaster`);
          // schemeId = null;
          // handleReset();
        } else {
          //handleReset();
        }
      } else {
        throw new Error(data.message || "Save failed");
        setLoading(false);
      }
    } catch (e) {
      toast.error(e.message);
      setLoading(false);
    }
  };

  const handleReset = () => {
    // move page to top
    const container = document.querySelector(".form-check");
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }


    if (isEditMode && schemeId) {
      // 🔥 Edit mode → Reset ONLY end date & status
      setEndDate(null);
      setStatus("Active");
      setSelectedStatusId(1);
      return;
    }


    // 🔄 Add mode → Full Reset
    setSelectedSCP(null);
    setSchemeName("");
    setSchemeType("Discount");
    setSelectedSchemeTypeId(1);
    setDiscountType("Percentage");
    setDiscountValue("");
    setStartDate(null);
    setEndDate(null);
    setDatePickerKey(k => k + 1); // force MaskedDateInput fields to clear
    setStatus("Active");
    setSelectedStatusId(1);
    setSelectedRegions([]);
    setSelectedScps([]);
    setSelectedProducts([]);
    setProductQuantities({});
    setSelectedFreeProducts([]);
    setFreeProductQuantities({});
    setGiftArticle("");
    setIsCombination(false);
    setRequiredErrors({});
    setError(null);
  };

  // --------------------------------------------------------------
  // 9. UI HELPERS
  // --------------------------------------------------------------
  const renderLabel = (id, label) => (
    <label htmlFor={id} className="form-label">
      {label}
      {requiredErrors[id] && <span className="text-danger"> *</span>}
    </label>
  );

  const selectStyles = {
    // control: (base, state) => ({
    //   ...base,
    //   cursor: "pointer",
    //   borderColor: state.isFocused ? "#e4e4e4" : base.borderColor,
    //   boxShadow: "none",
    //   minHeight: "40.5px",
    //   "&:hover": { borderColor: "#cfcfcf" },
    // }),
    control: (base, state) => ({
      ...base,
      cursor: 'pointer',
      borderColor: state.isFocused ? '#e4e4e4' : base.borderColor, // prevent blue border on focus
      boxShadow: state.isFocused ? 'none' : base.boxShadow, // remove default focus shadow
      '&:hover': {
        borderColor: '#cfcfcf', // optional hover border color
      },
    }),
    menu: base => ({ ...base, marginTop: -1 }),
  };

  const schemeTypeOptions = [
    { id: 1, value: "Discount", label: "Discount" },
    { id: 2, value: "Free Product", label: "Free Product" },
    { id: 3, value: "Gift Article", label: "Gift Article" },
  ];

  const statusOptions = [
    { id: 1, value: "Active", label: "Active" },
    { id: 2, value: "Inactive", label: "Inactive" },
  ];

  const handleProductQuantityBlur = (pid) => {
    setProductQuantities(prev => ({
      ...prev,
      [pid]: !prev[pid] ? "1" : prev[pid]
    }));
  };

  const handleFreeProductQuantityBlur = (pid) => {
    setFreeProductQuantities(prev => ({
      ...prev,
      [pid]: !prev[pid] ? "1" : prev[pid]
    }));
  };

  const handleBlur = (field, value) => {
    if (value && typeof value === "string") {
      const trimmedValue = value.trim();
      if (trimmedValue !== value) {
        handleChange(field, trimmedValue);
      }
    }
  };


  return (

    <>
      {loading && (
        <div
          style={{
            position: 'fixed', // Sit on top of the page content
            left: 0,
            top: 0,
            width: '100%', // Full width
            height: '100%', // Full height

            zIndex: 1000
          }}
        />
      )}
      <ToastContainer style={{ marginTop: "73px", fontSize: "14px", width: "40%" }} />
      <div className="main_container">
        <h3 className="formHeading" style={{ marginLeft: 0 }}>Scheme Master</h3>

        {error && <p className="text-danger mt-1">{error}</p>}
        <div className="row">
          {/* ROW 1 */}
          <div className="col-md-4">
            <div className="mb-3">
              {renderLabel("SCP", "SCP")}
              <div className="select-container_list">
                <Select
                  options={scpOptions}
                  value={selectedSCP}
                  onChange={opt => handleChange("SCP", opt)}
                  className="routing_select-box"
                  classNamePrefix="react-select"
                  onMenuOpen={() => setIsSelectOpen("SCP")}
                  onMenuClose={() => setIsSelectOpen(null)}
                  styles={selectStyles}
                  isDisabled={isDisabled}
                  placeholder="--Select--"
                  style={
                    isDisabled
                      ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                      : {}
                  }
                  components={{ SingleValue: CustomSingleValue }}
                />
                <div className="routing_icon-container" >
                  <i>{isSelectOpen === "SCP" ? <IoCaretUpOutline /> : <IoCaretDownOutline />}</i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              {renderLabel("SchemeName", "Scheme Name")}
              <input
                type="text"
                className="form-input"
                value={schemeName}
                onChange={e => handleChange("SchemeName", e.target.value)}
                onBlur={e => handleBlur("SchemeName", e.target.value)}
                disabled={isDisabled}
                style={
                  isDisabled
                    ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                    : {}
                }
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              {renderLabel("SchemeType", "Scheme Type")}
              <div className="select-container_list">
                <Select
                  options={schemeTypeOptions}
                  value={schemeTypeOptions.find(o => o.id === selectedSchemeTypeId)}
                  onChange={opt => handleChange("SchemeType", opt)}
                  className="routing_select-box"
                  classNamePrefix="react-select"
                  maxMenuHeight={140}
                  onMenuOpen={() => setIsSelectOpen("SchemeType")}
                  onMenuClose={() => setIsSelectOpen(null)}
                  styles={selectStyles}
                  isDisabled={isDisabled}
                  style={
                    isDisabled
                      ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                      : {}
                  }
                  components={{ SingleValue: CustomSingleValue }}
                />
                <div className="routing_icon-container">
                  <i>{isSelectOpen === "SchemeType" ? <IoCaretUpOutline /> : <IoCaretDownOutline />}</i>
                </div>
              </div>
            </div>
          </div>

          {/* ROW 2 */}
          <div className="col-md-4">
            <div className="mb-3">
              {renderLabel("StartDate", "Start Date")}
              <DatePicker
                key={`start-${datePickerKey}`}
                selected={startDate}
                onChange={handleStartDateChange}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                disabled={isDisabled}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                customInput={
                  <MaskedDateInput
                    className="form-input"
                    style={isDisabled ? { backgroundColor: "#f2f2f2", color: "#6c757d" } : {}}
                    minDate={new Date()}
                    onValidDate={(date) => handleStartDateChange(date)}
                    onInvalidDate={() => {
                      toast.error("Start Date cannot be a past date.");
                      setDatePickerKey(k => k + 1);
                    }}
                  />
                }
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              {renderLabel("EndDate", "End Date")}
              <DatePicker
                key={`end-${datePickerKey}`}
                selected={endDate}
                onChange={handleEndDateChange}
                startDate={startDate}
                endDate={endDate}
                minDate={startDate || new Date()}
                disabled={loading || !startDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                customInput={
                  <MaskedDateInput
                    className="form-input"
                    style={(!startDate || loading) ? { backgroundColor: "#f2f2f2", color: "#6c757d" } : {}}
                    minDate={startDate || new Date()}
                    onValidDate={(date) => handleEndDateChange(date)}
                    onInvalidDate={() => {
                      const msg = startDate
                        ? "End Date must be on or after the Start Date."
                        : "End Date cannot be a past date.";
                      toast.error(msg);
                      setDatePickerKey(k => k + 1);
                    }}
                  />
                }
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              {renderLabel("Status", "Status")}
              <div className="select-container_list">
                <Select
                  options={statusOptions}
                  value={statusOptions.find(o => o.id === selectedStatusId)}
                  onChange={opt => handleChange("Status", opt)}
                  className="routing_select-box"
                  classNamePrefix="react-select"
                  maxMenuHeight={100}
                  onMenuOpen={() => setIsSelectOpen("Status")}
                  onMenuClose={() => setIsSelectOpen(null)}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      cursor: 'pointer',
                      borderColor: state.isFocused ? '#e4e4e4' : base.borderColor,
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#cfcfcf',
                      },
                    }),
                  }}
                  isSearchable={false}
                  //isDisabled={isDisabled}
                  components={{ SingleValue: CustomSingleValue }}
                />
                <div className="routing_icon-container">
                  <i>{isSelectOpen === "Status" ? <IoCaretUpOutline /> : <IoCaretDownOutline />}</i>
                </div>
              </div>
            </div>
          </div>

          {/* ROW 3 */}
          <div className="col-md-6">
            <div className="mb-3">
              {renderLabel("Regions", "Route Name")}
              <div className="form-check scroll-box">
                {regions.length ? (
                  <>
                    <div>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="region-all"
                        checked={isAllChecked(selectedRegions, regions, "RUTL_ID")}
                        onChange={() => toggleAll(selectedRegions, setSelectedRegions, regions, "RUTL_ID")}
                        disabled={isDisabled}
                        style={
                          isDisabled
                            ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                            : {}
                        }
                      />
                      <label htmlFor="region-all" className="form-check-label">Select All</label>
                    </div>
                    {regions.map(r => (
                      <div key={r.RUTL_ID}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`region-${r.RUTL_ID}`}
                          checked={selectedRegions.includes(r.RUTL_ID)}
                          onChange={() => handleRegionChange(r.RUTL_ID)}
                          disabled={isDisabled}
                          style={
                            isDisabled
                              ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                              : {}
                          }
                        />
                        <label htmlFor={`region-${r.RUTL_ID}`} className="form-check-label" style={{ width: "auto", display: "inline-block" }}>{r.RUTL_Name}</label>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-muted">No Route</p>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              {renderLabel("Scps", "SCP Name")}
              <div className="form-check scroll-box">
                {scps.length ? (
                  <>
                    <div>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="scp-all"
                        checked={isAllChecked(selectedScps, scps, "SCPM_ID")}
                        onChange={() => toggleAll(selectedScps, setSelectedScps, scps, "SCPM_ID")}
                        disabled={isDisabled}
                        style={
                          isDisabled
                            ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                            : {}
                        }
                      />
                      <label htmlFor="scp-all" className="form-check-label">Select All</label>
                    </div>
                    {scps.map(s => (
                      <div key={s.SCPM_ID} className="word-wrap">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`scp-${s.SCPM_ID}`}
                          checked={selectedScps.includes(s.SCPM_ID)}
                          onChange={() => handleScpChange(s.SCPM_ID)}
                          disabled={isDisabled}
                          style={
                            isDisabled
                              ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                              : {}
                          }
                        />
                        <label htmlFor={`scp-${s.SCPM_ID}`} className="form-check-label" style={{ width: "auto", display: "inline-block" }}>{s.SCPM_Name}</label>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-muted">No SCPs</p>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="mb-3">
              {renderLabel("Products", "Product Name")}
              <div className="form-check scroll-box" ref={scrollRef}>
                {products.length ? (
                  <>
                    <div>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="product-all"
                        checked={isAllChecked(selectedProducts, products, "PL_ProductId")}
                        onChange={() => toggleAll(selectedProducts, setSelectedProducts, products, "PL_ProductId", productQuantities, setProductQuantities)}
                        disabled={isDisabled}
                        style={
                          isDisabled
                            ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                            : {}
                        }
                      />
                      <label htmlFor="product-all" className="form-check-label">Select All</label>
                    </div>
                    {products.map(p => (
                      <div key={p.PL_ProductId} className="d-flex justify-content-between align-items-start">
                        <div style={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`product-${p.PL_ProductId}`}
                            checked={selectedProducts.includes(p.PL_ProductId)}
                            onChange={() => handleProductChange(p.PL_ProductId)}
                            disabled={isDisabled}
                            style={{
                              marginTop: "3px",
                              flexShrink: 0,
                              ...(isDisabled
                                ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                                : {})
                            }}
                          />
                          <label htmlFor={`product-${p.PL_ProductId}`} className="form-check-label me-2" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                            {p.PL_ProductName}
                          </label>
                        </div>
                        {selectedProducts.includes(p.PL_ProductId) && (
                          <input
                            type="text"
                            className="form-input"
                            style={{
                              width: "80px",
                              ...(isDisabled && {
                                backgroundColor: "#f2f2f2",
                                color: "#6c757d",
                                // cursor: "not-allowed",
                              }),
                            }}
                            value={productQuantities[p.PL_ProductId] ?? ""}
                            onChange={e => handleProductQuantityChange(p.PL_ProductId, e.target.value)}
                            onBlur={() => handleProductQuantityBlur(p.PL_ProductId)}
                            min="1"
                            disabled={isDisabled}
                          />
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-muted">No Products</p>
                )}
              </div>

              {selectedProducts.filter(id => id !== "All").length > 1 && (
                <div className="mt-3 p-2 bg-light border rounded">
                  <div className="form-check form-switch p-2 d-flex justify-content-between align-items-center">
                    <label className="form-check-label">
                      <strong>Combination Required</strong>
                    </label>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="combination-switch"
                      checked={isCombination}
                      onChange={e => setIsCombination(e.target.checked)}
                      disabled={isDisabled}
                      style={
                        isDisabled
                          ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                          : { cursor: "pointer" }
                      }
                    />

                  </div>
                  <small className="text-muted d-block mt-1">
                    {isCombination
                      ? "SCP must buy ALL selected products"
                      : "SCP can buy ANY of the selected products"}
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* OFFER FIELD */}
          <div className="col-md-4">
            <div className="mb-3">
              {schemeType === "Discount" && (
                <div>
                  {renderLabel("Discount", "Discount")}
                  <div className="discount-field">
                    <input
                      type="text"
                      className="discount-input"
                      value={discountValue}
                      onChange={e => handleChange("DiscountValue", e.target.value)}
                      min="0"
                      max={discountType === "Percentage" ? "100" : undefined}
                      disabled={isDisabled}

                      onWheel={e => e.target.blur()}
                      style={{
                        flex: 1,
                        ...(isDisabled && {
                          backgroundColor: "#f2f2f2",
                          color: "#6c757d",
                          // cursor: "not-allowed",
                        }),
                      }}
                    />
                    <div className="discount-toggle" role="group">
                      <button
                        type="button"
                        className={`toggle-btn ${discountType === "Percentage" ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                        onClick={() => handleChange("DiscountType", "Percentage")}
                        disabled={isDisabled}
                      // style={
                      //   isDisabled
                      //     ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                      //     : {}
                      // }
                      >
                        %
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${discountType === "Amount" ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                        onClick={() => handleChange("DiscountType", "Amount")}
                        disabled={isDisabled}
                      // style={
                      //   isDisabled
                      //     ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                      //     : {}
                      // }
                      >
                        Rs.
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {schemeType === "Free Product" && (
                <>
                  {renderLabel("FreeProducts", "Free Product")}
                  <div className="form-check scroll-box">
                    {products.length ? (
                      <>
                        <div>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="free-product-all"
                            checked={isAllChecked(selectedFreeProducts, products, "PL_ProductId")}
                            onChange={() => toggleAll(selectedFreeProducts, setSelectedFreeProducts, products, "PL_ProductId", freeProductQuantities, setFreeProductQuantities, 1)}
                            disabled={isDisabled}
                            style={
                              isDisabled
                                ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                                : {}
                            }
                          />
                          <label htmlFor="free-product-all" className="form-check-label">Select All</label>
                        </div>
                        {products.map(p => (
                          <div key={p.PL_ProductId} className="d-flex justify-content-between align-items-start">
                            <div style={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`free-product-${p.PL_ProductId}`}
                                checked={selectedFreeProducts.includes(p.PL_ProductId)}
                                onChange={() => handleFreeProductChange(p.PL_ProductId)}
                                disabled={isDisabled}
                                style={{
                                  marginTop: "3px",
                                  flexShrink: 0,
                                  ...(isDisabled
                                    ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                                    : {})
                                }}
                              />
                              <label htmlFor={`free-product-${p.PL_ProductId}`} className="form-check-label me-2" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                                {p.PL_ProductName}
                              </label>
                            </div>
                            {selectedFreeProducts.includes(p.PL_ProductId) && (
                              <input
                                type="text"
                                className="form-input"
                                style={{
                                  width: "80px",
                                  ...(isDisabled && {
                                    backgroundColor: "#f2f2f2",
                                    color: "#6c757d",
                                    // cursor: "not-allowed",
                                  }),
                                }}
                                value={freeProductQuantities[p.PL_ProductId] ?? "1"}
                                onChange={e => handleFreeProductQuantityChange(p.PL_ProductId, e.target.value)}
                                onBlur={() => handleFreeProductQuantityBlur(p.PL_ProductId)}

                                min="1"
                                disabled={isDisabled}
                              />
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <p className="text-muted">No Free Products</p>
                    )}
                  </div>
                </>
              )}
              {schemeType === "Gift Article" && (
                <>
                  {renderLabel("GiftArticle", "Gift Article")}
                  <input
                    type="text"
                    className="form-input"
                    value={giftArticle}
                    onChange={e => handleChange("GiftArticle", e.target.value)}
                    onBlur={e => handleBlur("GiftArticle", e.target.value)}
                    disabled={isDisabled}
                    style={
                      isDisabled
                        ? { backgroundColor: "#f2f2f2", color: "#6c757d" }
                        : {}
                    }
                  />
                </>
              )}
            </div>
          </div>

          {/* DESCRIPTION */}
          <div className="col-md-4">
            <div className="mb-3">
              {renderLabel("SchemeDescription", "Scheme Description")}
              <textarea
                className="form-input"
                value={schemeDescription}
                rows={4}
                style={{ resize: "none", overflowY: "auto", height: "auto" }}
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      {/* ================== MODALS ================== */}
      {showconfimationModal && (
        <div className="popup_overlay">
          <div className="resetpopup">
            <h1>:: User Confirmation ::</h1>
            <h4>
              Are you sure you want to {isEditMode ? "edit" : "save"} the Scheme : {schemeName} ?
            </h4>

            <div className="popup_btn_container">
              <button
                className="yes_btn"
                onClick={() => {
                  setshowconfimationModal(false);
                  handleSave();
                }}
              >
                YES
              </button>

              <button
                className="no_btn"
                onClick={() => { setshowconfimationModal(false); setLoading(false); }}
              >
                NO
              </button>

            </div>
          </div>
        </div>
      )}

      <div className="button-container">
        <button
          className="save_btn"
          type="button"
          onClick={handleConfirmation}
        //disabled={isDisabled}
        >
          SAVE
        </button>
        <button
          className="reset_btn"
          type="button"
          onClick={handleReset}

        >
          RESET
        </button>
        <Link to="/SchemeList" className="list_btn" type="button">
          LIST
        </Link>
      </div>
    </>
  );
};

export default SchemeMaster;